/**
 * Growth-status classification. Mirrors backend app/services/growth_service.py.
 * Keep these in sync — single source of truth used by every page that
 * renders a growth-status color/label.
 *
 * A child can carry more than one status simultaneously (e.g. Wasting +
 * Stunting), so the API returns ``growthStatuses: string[]``. The Growth
 * column renders one pill per status; only "Normal" is mutually exclusive
 * with the others and is shown alone when no alert band fires.
 */

export const GROWTH_STATUS = {
    NORMAL: 'Normal',
    UNDERWEIGHT: 'Underweight',
    SEVERELY_UNDERWEIGHT: 'Severely Underweight',
    OVERWEIGHT: 'Overweight',
    OBESE: 'Obese',
    WASTING: 'Wasting',
    SEVERE_WASTING: 'Severe Wasting',
    STUNTING: 'Stunting',
    SEVERE_STUNTING: 'Severe Stunting',
    UNKNOWN: 'Unknown',
};

export const GROWTH_STATUSES = Object.values(GROWTH_STATUS);

/**
 * Umbrella hierarchy used by the Growth column filter. Selecting
 * "Underweight" on the Profiles page also matches "Severely Underweight";
 * selecting "Normal" matches only the explicit Normal pill.
 *
 * Must mirror app/services/growth_service.GROWTH_UMBRELLA.
 */
export const GROWTH_UMBRELLA = {
    Underweight: new Set(['Underweight', 'Severely Underweight']),
    Overweight: new Set(['Overweight', 'Obese']),
    Wasting: new Set(['Wasting', 'Severe Wasting']),
    Stunting: new Set(['Stunting', 'Severe Stunting']),
};

/**
 * Map a growth status to a <StatusBadge severity="..."/> value.
 *   success    → on track         (Normal)
 *   warning    → above target     (Overweight)
 *   danger     → below target     (Underweight, Severely Underweight, Obese,
 *                                 Wasting, Severe Wasting, Stunting,
 *                                 Severe Stunting)
 *   neutral    → data missing     (Unknown)
 */
export function growthSeverity(status) {
    switch (status) {
        case GROWTH_STATUS.NORMAL:
            return 'success';
        case GROWTH_STATUS.OVERWEIGHT:
            return 'warning';
        case GROWTH_STATUS.UNDERWEIGHT:
        case GROWTH_STATUS.SEVERELY_UNDERWEIGHT:
        case GROWTH_STATUS.OBESE:
        case GROWTH_STATUS.WASTING:
        case GROWTH_STATUS.SEVERE_WASTING:
        case GROWTH_STATUS.STUNTING:
        case GROWTH_STATUS.SEVERE_STUNTING:
            return 'danger';
        case GROWTH_STATUS.UNKNOWN:
        default:
            return 'neutral';
    }
}

/** Default fallback when the backend sends null/empty. */
export const GROWTH_STATUS_DEFAULT = GROWTH_STATUS.UNKNOWN;
